import React from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core";

type PrintableComponentProps = {
    reference: React.Ref<any>;
    Content: React.ComponentType;
    Header?: React.ComponentType;
    Footer?: React.ComponentType;
    style?: React.CSSProperties;
};

/**
 * PrintableComponent: A reusable component for handling multi-page printing
 * with a consistent header and footer on each page.
 * https://medium.com/@balindambajpai/printing-in-react-adding-headers-and-footers-to-multi-page-documents-b952ecc8542d
 *
 * @param {JSX.Element} Header - The header content
 * @param {JSX.Element} Footer - The footer content
 * @param {JSX.Element} Content - The main printable content
 * @param {React.Ref} reference - Reference for react-to-print
 */
const PrintableComponent = ({ Header, Footer, Content, reference, style }: PrintableComponentProps) => {
    const classes = useStyles();

    return (
        <div className={classes.printableComponent} style={style} ref={reference}>
            <table>
                <thead>
                    <tr>
                        <td>
                            <div className="header-space">&nbsp;</div>
                        </td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <div className="content">
                                <Content />
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td>
                            <div className="footer-space">&nbsp;</div>
                        </td>
                    </tr>
                </tfoot>
            </table>
            <div className="header">
                <Header />
            </div>
            {Footer && (
                <div className="footer">
                    <Footer />
                </div>
            )}
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        printableComponent: {
            display: "none", // Default - Only display when print
            "@media print": {
                display: "block",
                "& .header-space, .footer-space": {
                    height: "150px",
                    width: "100%",
                    left: 0
                },
                "& .header, .footer": {
                    position: "fixed",
                    left: 0,
                    width: "100%"
                },
                "& .header": {
                    top: 0
                },
                "& .footer": {
                    bottom: 0
                },
                "& .content": {
                    width: "100%",
                }
            }
        },
    }),
);

export default PrintableComponent;
